import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DataTable } from "@outpave/ds";
import type { DataColumn } from "@outpave/ds";
import RewardRuleStatusChip from "./RewardRuleStatusChip";
import type { PlatformRewardRule } from "./types";
import {
  ruleCompanies,
  ruleTarget,
  ruleWindow,
} from "utils/rewards/rewardRules";

interface EarnRulesTableProps {
  rules: PlatformRewardRule[];
  loading: boolean;
  error: boolean;
  emptyState: string;
  onOpen?: (rule: PlatformRewardRule) => void;
}

const EarnRulesTable: React.FC<EarnRulesTableProps> = ({
  rules,
  loading,
  error,
  emptyState,
  onOpen,
}) => {
  const { t } = useTranslation();
  const columns: DataColumn<PlatformRewardRule>[] = useMemo(
    () => [
      { id: "name", header: t("Rule"), cell: (rule) => rule.name, flex: true },
      {
        id: "target",
        header: t("Applies to"),
        cell: (rule) => ruleTarget(rule, t),
      },
      {
        id: "companies",
        header: t("Accounts"),
        cell: (rule) => ruleCompanies(rule, t),
      },
      {
        id: "rate",
        header: t("Rate"),
        align: "right",
        cell: (rule) => `${rule.ratePercent}%`,
      },
      { id: "when", header: t("When"), cell: (rule) => ruleWindow(rule, t) },
      {
        id: "priority",
        header: t("Priority"),
        align: "right",
        cell: (rule) => rule.priority,
      },
      {
        id: "status",
        header: t("Status"),
        cell: (rule) => <RewardRuleStatusChip status={rule.status} />,
      },
    ],
    [t],
  );

  return (
    <DataTable<PlatformRewardRule>
      columns={columns}
      rows={rules}
      getRowKey={(rule) => rule.id}
      loading={loading}
      emptyState={emptyState}
      errorState={error ? t("RewardRulesLoadError") : undefined}
      onRowClick={onOpen}
    />
  );
};

export default EarnRulesTable;
